import * as api from "./adapter";
import downloadFile from "./downloadFile";
import { convertItem, convertItems } from "./convertors/users";
import serializeDataToQueryString from "./serializeDataToQueryString";
import { convertItems as convertAppFormLinkItems } from "./convertors/experienceVerifications";
import { buildFilterQuery } from "./utils/users";

export default {
  /**
   * getCompanies
   * @returns {Promise<*>}
   */
  index: async ({
    pagination,
    sort,
    filter,
    filterType,
    search: name,
    fields = {},
  }) => {
    // `last_expirience` (Phase A3) and `external_applications`
    // (Phase D) were both promoted from JR relationships to inline
    // UserResource attributes in the 2026-05-19 user-search perf
    // pass. The JR include-pipeline for both did expensive
    // multi-table JOINs to pull data the FE consumer only formatted
    // into a label / attachment popover. Drop both from `include`;
    // they arrive as `attributes.last_expirience` /
    // `attributes.external_applications` (see `convertors/users.js`).
    const query = {
      include: "role",
      filter: buildFilterQuery(filter, filterType, name),
      page: { size: pagination.itemsPerPage, number: pagination.current },
      fields,
    };

    if (sort && sort.field)
      query["sort"] = `${sort.direction === "asc" ? "" : "-"}${sort.field}`;

    const { data } = await api.read("users", { query });

    return convertItems(data);
  },
  show: async (id) => {
    const { data: json } = await api.read(`users/${id}`, {
      query: { include: "expiriences,role" },
    });

    return convertItem(json.data, json.included);
  },
  update: async (id, attributes) => {
    const { data: json } = await api.update(
      {
        id,
        type: "users",
        attributes,
      },
      { query: { include: "expiriences,role" } }
    );

    return convertItem(json.data, json.included);
  },
  create: async (attributes) => {
    const { data: json } = await api.create(
      {
        type: "users",
        attributes,
      },
      { query: { include: "expiriences,role" } }
    );

    return convertItem(json.data, json.included);
  },
  delete: (id) => api.destroy({ id, type: "users" }),

  getContractLink: async ({
    timeplanId,
    fullContract,
    asDocx = false,
    params = {},
  }) => {
    const qParams = [];
    if (fullContract) qParams.push("full=true");
    if (asDocx) qParams.push("as_docx=true");
    qParams.push(serializeDataToQueryString(params));

    const response = await api.read(
      `/print_contracts/${timeplanId}?${qParams.join("&")}`,
      {}
    );

    return {
      ...response.data.data.attributes,
      uri: response.data.data.attributes.xls_file,
      name: response.data.data.attributes.file_file_name,
    };
  },
  assignTag: async ({ filter, filterType, search: name, tag, userId }) => {
    await api.read("users/assign_tag", {
      query: {
        tag,
        userId,
        filter: buildFilterQuery(filter, filterType, name),
        page: { size: 10000, number: 1 },
      },
    });
    return true;
  },
  removeTag: async ({ filter, filterType, search: name, tag, userId }) => {
    await api.read("users/remove_tag", {
      query: {
        tag,
        userId,
        filter: buildFilterQuery(filter, filterType, name),
        page: { size: 10000, number: 1 },
      },
    });

    return true;
  },
  downloadXlsx: async ({ filter, filterType, search: name }) => {
    const response = await api.read("users/index_xlsx", {
      query: {
        // `last_expirience` and `external_applications` are inline
        // attributes now (Phases A3 / D).
        include: "role",
        filter: buildFilterQuery(filter, filterType, name),
        page: { size: 200, number: 1 },
      },
    });

    return {
      ...response.data.data.attributes,
      uri: response.data.data.attributes.xls_file,
      name: response.data.data.attributes.file_file_name,
    };
  },

  checkDocumentMatrix: ({ query }) =>
    api.read("users/check_document_matrix", { query }),

  // CREW-1195 — the same three parameters as the check above, answered with
  // the files attached. Goes through downloadFile because the endpoint streams
  // a binary body and devise-token auth lives in headers, not cookies.
  downloadDocumentMatrixArchive: ({ user_id, matrix_id, role_id }) => {
    const query = new URLSearchParams({ user_id, matrix_id, role_id }).toString();
    return downloadFile(`users/document_matrix_archive?${query}`, "documents.zip");
  },

  updateFromUC: async (id) => {
    return await api.read(`users/${id}/update_from_uc`);
  },

  updateFromCW: async (id) => {
    return await api.read(`users/${id}/update_from_cw`);
  },

  getAppFormLinks: async (id) => {
    const { data } = await api.read(`users/${id}/app_form_links`, {
      query: {
        include: "author",
        sort: "-created_at",
        page: { size: 999 },
      },
    });
    return convertAppFormLinkItems(data);
  },

  assignToOwnerPool: async (userId, ownerId) => {
    return await api.create(
      {
        owner_id: ownerId,
      },
      { url: `users/${userId}/assign_to_owner_pool` }
    );
  },

  removeFromOwnerPool: async (userId, removalReasonId, additionalReasonIds = []) => {
    return await api.create(
      {
        removal_reason_id: removalReasonId,
        additional_reason_ids: additionalReasonIds,
      },
      { url: `users/${userId}/remove_from_owner_pool` }
    );
  },

  getOwnerPoolHistory: async (userId) => {
    const { data } = await api.read(`users/${userId}/owner_pool_history`);
    return data;
  },

  checkSeafarerUniqueness: async ({ name, surname, date_of_birth, email, mobile_phone }) => {
    return await api.create(
      {
        name,
        surname,
        date_of_birth,
        email,
        mobile_phone,
      },
      { url: 'users/check_seafarer_uniqueness' }
    );
  },
};
